import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { tescomIssueSeed } from "./tescom-seed-content";

// ─── TESCOM Color Palette ────────────────────────────────────────────────────
// Primary: Deep Blue (Education/Authority)
// Accent: Gold (Excellence)
// Secondary: Green (Growth)

// ═══════════════════════════════════════════════════════════════════════════
// STRAPI INTEGRATION — types, fetch, and mapper all live here so this file
// is self-contained. No separate api/ folder or types/ file needed.
// ═══════════════════════════════════════════════════════════════════════════

// ⚠️ Adjust this to match your bundler's env var convention:
//   Vite    → import.meta.env.VITE_STRAPI_API_URL
//   Next.js → process.env.NEXT_PUBLIC_STRAPI_API_URL
//   CRA     → process.env.REACT_APP_STRAPI_API_URL
const STRAPI_URL = (import.meta as any).env?.VITE_STRAPI_API_URL ?? "http://localhost:1337";

// ─── FE-facing types (what every layout component below consumes) ─────────

type PageLayout =
  | "cover"
  | "editor"
  | "article"
  | "article-left"
  | "article-right"
  | "gallery"
  | "feature"
  | "success-story"
  | "back-cover"
  | "continuation"
  | "text-only";

interface TESCOMPage {
  id: number;
  layout: PageLayout;
  pageNumber: number;
  title: string;
  subtitle?: string;
  category?: string;
  author?: string;
  body?: string;
  body2?: string;
  image?: string;
  image2?: string;
  imageCaption?: string;
  galleryImages?: string[];
  pullQuote?: string;
  pullQuoteAuthor?: string;
  sidebarItems?: Array<{ label: string; value: string }>;
  sections?: Array<{ heading: string; body: string }>;
  teasers?: Array<{ title: string; page: string }>;
}

// ─── Raw Strapi response shapes ─────────────────────────────────────────────

interface StrapiMedia {
  id: number;
  url: string;
  alternativeText?: string | null;
}

interface StrapiCategory {
  id: number;
  name: string;
  slug?: string;
  color?: string;
}

interface StrapiAuthor {
  id: number;
  username: string;
  email?: string;
}

interface StrapiSection {
  id: number;
  heading: string;
  body: string;
}

interface StrapiTeaser {
  id: number;
  title: string;
  page: string;
}

interface StrapiSidebarItem {
  id: number;
  label: string;
  value: string;
}

interface StrapiPage {
  id: number;
  pageNumber: number;
  title: string;
  layout: string; // raw, possibly invalid — normalized in the mapper below
  subtitle?: string | null;
  body?: string | null;
  body2?: string | null;
  imageCaption?: string | null;
  pullQuote?: string | null;
  pullQuoteAuthor?: string | null;
  category?: StrapiCategory | null;
  author?: StrapiAuthor | null;
  image?: StrapiMedia | null;
  image2?: StrapiMedia | null;
  galleryImages?: StrapiMedia[] | null;
  sidebarItems?: StrapiSidebarItem[] | null;
  sections?: StrapiSection[] | null;
  teasers?: StrapiTeaser[] | null;
}

interface StrapiIssue {
  id: number;
  title: string;
  slug?: string;
  publishedDate?: string;
  pages: StrapiPage[];
}

// ─── Layout normalization ───────────────────────────────────────────────────
// Strapi's `layout` field can contain values your FE has no component for
// (e.g. the generic "article" seen on real data). Fix the Strapi enum to
// only allow the 11 valid values long-term — this is a safety net.

const LAYOUT_ALIASES: Record<string, PageLayout> = {
  article: "article-left"
};

const VALID_LAYOUTS = new Set<PageLayout>([
  "cover",
  "editor",
  "article",
  "article-left",
  "article-right",
  "gallery",
  "feature",
  "success-story",
  "back-cover",
  "continuation",
  "text-only"
]);

function normalizeLayout(raw: string, pageId: number): PageLayout {
  if (VALID_LAYOUTS.has(raw as PageLayout)) return raw as PageLayout;
  const aliased = LAYOUT_ALIASES[raw];
  if (aliased) {
    console.warn(
      `[TESCOMMagazine] Page ${pageId}: layout "${raw}" isn't a valid FE layout, mapped to "${aliased}". Fix the Strapi layout enum for this page.`
    );
    return aliased;
  }
  console.warn(
    `[TESCOMMagazine] Page ${pageId}: unrecognized layout "${raw}", falling back to "text-only".`
  );
  return "text-only";
}

function mediaUrl(media?: StrapiMedia | null): string | undefined {
  return media?.url ?? undefined;
}

function omitNullish(obj: TESCOMPage): TESCOMPage {
  const out: Partial<TESCOMPage> = {};
  (Object.keys(obj) as Array<keyof TESCOMPage>).forEach((key) => {
    const val = obj[key];
    if (val === null || val === undefined) return;
    if (Array.isArray(val) && val.length === 0) return;
    out[key] = val as never;
  });
  return out as TESCOMPage;
}

function mapStrapiPageToTESCOMPage(page: StrapiPage): TESCOMPage {
  const mapped: TESCOMPage = {
    id: page.id,
    layout: normalizeLayout(page.layout, page.id),
    pageNumber: page.pageNumber,
    title: page.title,
    subtitle: page.subtitle ?? undefined,
    category: page.category?.name ?? undefined,
    author: page.author?.username ?? undefined,
    body: page.body ?? undefined,
    body2: page.body2 ?? undefined,
    image: mediaUrl(page.image),
    image2: mediaUrl(page.image2),
    imageCaption: page.imageCaption ?? undefined,
    galleryImages: page.galleryImages?.map((img) => mediaUrl(img)).filter(Boolean) as
      | string[]
      | undefined,
    pullQuote: page.pullQuote ?? undefined,
    pullQuoteAuthor: page.pullQuoteAuthor ?? undefined,
    sidebarItems: page.sidebarItems?.length
      ? page.sidebarItems.map((item) => ({ label: item.label, value: item.value }))
      : undefined,
    sections: page.sections?.length
      ? page.sections.map((s) => ({ heading: s.heading, body: s.body }))
      : undefined,
    teasers: page.teasers?.length
      ? page.teasers.map((t) => ({ title: t.title, page: t.page }))
      : undefined
  };
  return omitNullish(mapped);
}

function mapStrapiIssueToPages(issue: StrapiIssue): TESCOMPage[] {
  if (!issue?.pages) return [];
  return [...issue.pages]
    .sort((a, b) => a.pageNumber - b.pageNumber)
    .map(mapStrapiPageToTESCOMPage);
}
type SeedPage = (typeof tescomIssueSeed)["pages"][number] &
  Partial<Omit<TESCOMPage, "id" | "layout" | "pageNumber" | "title">>;

function mapSeedPageToTESCOMPage(
  rawPage: (typeof tescomIssueSeed)["pages"][number],
  index: number
): TESCOMPage {
  const page = rawPage as SeedPage;
  const mapped: TESCOMPage = {
    id: index + 1,
    layout: normalizeLayout(page.layout, index + 1),
    pageNumber: page.pageNumber,
    title: page.title,
    subtitle: page.subtitle,
    category: page.category,
    author: page.author,
    body: page.body,
    body2: page.body2,
    image: page.image,
    image2: page.image2,
    imageCaption: page.imageCaption,
    galleryImages: page.galleryImages,
    pullQuote: page.pullQuote,
    pullQuoteAuthor: page.pullQuoteAuthor,
    sidebarItems: page.sidebarItems,
    sections: page.sections,
    teasers: page.teasers
  };
  return omitNullish(mapped);
}
function loadSeedIssue(): { pages: TESCOMPage[]; title: string } {
  return {
    pages: tescomIssueSeed.pages.map(mapSeedPageToTESCOMPage),
    title: tescomIssueSeed.title
  };
}

// ─── Fetch ──────────────────────────────────────────────────────────────────

class StrapiFetchError extends Error {
  constructor(
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = "StrapiFetchError";
  }
}

/**
 * Fetch issues from the list endpoint: GET /api/issues
 * This is your custom `find()` controller action — it returns an array
 * directly (not wrapped in { data: [...] }), with `pages` deep-populated.
 */
async function fetchIssuesList(): Promise<StrapiIssue[]> {
  const res = await fetch(`${STRAPI_URL}/api/issues`);
  if (!res.ok) {
    throw new StrapiFetchError(
      `Failed to fetch issues: ${res.status} ${res.statusText}`,
      res.status
    );
  }
  const json = await res.json();
  // Your find() action returns the array directly. Handle the standard
  // Strapi { data: [...] } wrapper too, in case that ever changes.
  const issues: StrapiIssue[] = Array.isArray(json) ? json : json?.data ?? [];
  return issues;
}

/**
 * Resolve a single issue to display.
 *   - No issueId passed → most recently published issue (by publishedDate, falling back to id)
 *   - issueId passed → matches against numeric id (or slug, if you pass a string slug)
 */
async function resolveIssue(issueId?: number | string): Promise<StrapiIssue | null> {
  const issues = await fetchIssuesList();
  if (issues.length === 0) return null;

  if (issueId !== undefined) {
    const match = issues.find((issue) => issue.id === issueId || issue.slug === issueId);
    if (!match) {
      throw new StrapiFetchError(
        `Issue "${issueId}" not found among ${issues.length} returned issues.`
      );
    }
    return match;
  }

  // No specific issue requested — pick the most recently published one.
  const sorted = [...issues].sort((a, b) => {
    const dateA = a.publishedDate ?? "";
    const dateB = b.publishedDate ?? "";
    if (dateA && dateB) return dateB.localeCompare(dateA);
    return b.id - a.id;
  });
  return sorted[0];
}

// ─── Shared Header ────────────────────────────────────────────────────────────

function PageHeader() {
  return (
    <div className="flex items-center justify-center gap-3 py-2 px-6 border-b border-blue-200 bg-white shrink-0">
      <span className="text-[9px] font-bold text-blue-900 uppercase tracking-widest">
        Ogun State
      </span>
      <span className="text-[9px] font-bold text-blue-900 uppercase tracking-widest">|</span>
      <span className="text-[9px] font-bold text-amber-700 uppercase tracking-widest">TESCOM</span>
    </div>
  );
}

// ─── Tricolor Footer ─────────────────────────────────────────────────────────

function TriFooter({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="shrink-0">
      <div className="flex h-1.5">
        <div className="flex-1 bg-blue-900" />
        <div className="flex-1 bg-amber-500" />
        <div className="flex-1 bg-green-700" />
      </div>
      <div className="bg-white px-6 py-1.5 flex justify-between items-center border-t border-gray-200">
        <span className="text-[9px] tracking-widest uppercase text-gray-400">{issueLabel}</span>
        <span className="text-[9px] font-bold text-gray-400 tabular-nums">
          PAGE {String(page.pageNumber).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

// ─── Drop Cap ─────────────────────────────────────────────────────────────────

function DropCap({ letter, color = "bg-blue-900" }: { letter: string; color?: string }) {
  return (
    <span
      className={`float-left mr-2 mt-0.5 w-7 h-7 ${color} text-white font-black text-base flex items-center justify-center shrink-0`}
      style={{ fontFamily: "Georgia, serif", lineHeight: 1 }}
    >
      {letter}
    </span>
  );
}

// ─── Category Badge ───────────────────────────────────────────────────────────

function CatBadge({ label }: { label: string }) {
  return (
    <span className="inline-block text-[9px] bg-blue-900 text-white px-2 py-0.5 uppercase tracking-widest font-bold mb-2">
      {label}
    </span>
  );
}

// ─── Body Text ─────────────────────────────────────────────────────────────────

function BodyText({
  text,
  className = "",
  dropCap = false,
  size = "text-[12px]"
}: {
  text: string;
  className?: string;
  dropCap?: boolean;
  size?: string;
}) {
  const paras = text.split("\n\n").filter(Boolean);
  return (
    <div className={`space-y-2 ${className}`}>
      {paras.map((p, i) => (
        <p
          key={i}
          className={`${size} leading-relaxed text-gray-700`}
          style={{ fontFamily: "'Times New Roman', serif", textAlign: "justify" }}
        >
          {i === 0 && dropCap ? (
            <>
              <DropCap letter={p[0]} />
              {p.slice(1)}
            </>
          ) : (
            p
          )}
        </p>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT COMPONENTS
// (unchanged from your original — only the `page` prop type changed, since it
//  now imports TESCOMPage from ../types/tescom instead of a local declaration)
// ─────────────────────────────────────────────────────────────────────────────

function CoverLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <div className="bg-blue-900 text-amber-300 text-[9px] tracking-[0.3em] uppercase font-bold text-center py-1">
        {page.category}
      </div>

      <div className="px-4 pt-3 pb-1 flex items-end gap-3 border-b border-gray-200">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-green-700 rounded-full flex items-center justify-center shrink-0 shadow-md">
          <span className="text-white font-black text-lg" style={{ fontFamily: "Georgia,serif" }}>
            🎓
          </span>
        </div>
        <div className="flex-1">
          <h1
            className="text-blue-900 leading-none"
            style={{
              fontFamily: "Georgia,serif",
              fontSize: "3.2rem",
              fontStyle: "italic",
              fontWeight: 700
            }}
          >
            Magazine
          </h1>
          <p className="text-[8px] text-gray-600 uppercase tracking-widest leading-tight mt-0.5">
            {page.subtitle}
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border-b border-amber-200 px-4 py-1 flex gap-4 text-[9px] text-blue-900 font-semibold">
        <span>✉ info@tesercom.gov.ng</span>
        <span>Ogun State Teaching Service Commission</span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-col w-3/5 border-r border-gray-200">
          <div className="relative overflow-hidden" style={{ height: "42%" }}>
            <img src={page.image} alt="cover" className="w-full h-full object-cover" />
          </div>
          <div className="p-4 flex flex-col flex-1">
            <h2
              className="text-blue-900 font-black leading-tight mb-2"
              style={{ fontFamily: "Georgia,serif", fontSize: "1.35rem" }}
            >
              {page.body}
            </h2>
            <BodyText text={page.body2 ?? ""} dropCap size="text-[11px]" />
          </div>
        </div>

        <div className="w-2/5 flex flex-col">
          <div className="flex-1 bg-blue-50 p-3 flex flex-col justify-end">
            <div className="flex-1 relative overflow-hidden mb-3 rounded">
              <img
                src="data:image/webp;base64,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"
                alt="teaching"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-2 border-t border-blue-300 pt-2">
              {[
                {
                  t: "Teacher Monitoring Excellence Program Achieves 87% Competency Rate",
                  p: "03"
                },
                { t: "Induction Programme Transforms School Leadership Development", p: "05" },
                { t: "TESCOM Launches Comprehensive Digital Transformation Initiative", p: "09" }
              ].map((item, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-[9px] font-bold text-white bg-blue-900 px-1 py-0.5 shrink-0">
                    PAGE {item.p}
                  </span>
                  <p className="text-[9px] text-gray-800 leading-snug font-semibold">{item.t}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function EditorLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 p-5 overflow-y-auto border-r border-gray-200">
          <h2
            className="text-2xl font-black text-gray-900 mb-3"
            style={{ fontFamily: "Georgia,serif" }}
          >
            {page.title}
          </h2>
          <BodyText text={page.body ?? ""} dropCap />
          <div className="mt-4 flex items-center gap-3 border-t border-gray-200 pt-3">
            <img
              src={page.image}
              alt={page.author}
              className="w-14 h-14 rounded-full object-cover border-2 border-amber-500"
            />
            <div>
              <p
                className="text-sm font-bold text-gray-900"
                style={{ fontFamily: "Georgia,serif" }}
              >
                {page.author}
              </p>
              <p className="text-[10px] text-gray-500 italic">Communications, TESCOM Ogun</p>
            </div>
          </div>
        </div>

        <div className="w-44 shrink-0 flex flex-col">
          <div className="bg-blue-900 text-white p-3 mb-0">
            <h3 className="text-[10px] tracking-widest uppercase font-black text-amber-300 text-center mb-3">
              EDITORIAL
              <br />
              TEAM
            </h3>
            {page.sidebarItems?.map((item, i) => (
              <div key={i} className="mb-3 text-center border-b border-blue-700 pb-2">
                <p className="text-[9px] text-amber-300 uppercase tracking-wider mb-0.5">
                  {item.label}
                </p>
                <p className="text-[10px] text-white font-semibold leading-snug whitespace-pre-line">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
          <div className="bg-green-700 text-white p-3 flex-1">
            <h3 className="text-[10px] tracking-widest uppercase font-black text-center mb-3">
              TESCOM
              <br />
              LEADERSHIP
            </h3>
            {[
              ["Apostle Abiodun Sanyaolu", "Chairman"],
              ["Hon. Adeola Akintande ", "Commissioner I"],
              ["Adebajo Adesegun", "Commissioner II"],
              ["Olanrewaju Majekodunmi", "Commissioner III"],
              ["Hon. Adeleke SalmonHon", "Ex Officio "],
              ["Hon (Mrs.) Atinuke Bello", "Ex Officio"]
            ].map(([name, role], i) => (
              <div key={i} className="mb-1.5 text-center">
                <p className="text-[9px] font-semibold leading-snug">{name}</p>
                <p className="text-[8px] text-green-200 leading-snug">{role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function FeatureLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="relative shrink-0 overflow-hidden" style={{ height: "34%" }}>
        <img src={page.image} alt={page.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {page.category && <CatBadge label={page.category} />}
          <h2
            className="text-white font-black leading-tight"
            style={{ fontFamily: "Georgia,serif", fontSize: "1.25rem" }}
          >
            {page.title}
          </h2>
          {page.subtitle && (
            <p className="text-amber-300 text-[10px] italic mt-0.5">{page.subtitle}</p>
          )}
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden gap-0">
        <div className="flex-1 p-4 overflow-y-auto border-r border-gray-200">
          <BodyText text={page.body ?? ""} dropCap />
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          <BodyText text={page.body2 ?? ""} />
        </div>
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function ArticleRightLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="px-5 pt-4 pb-2 border-b border-gray-200 shrink-0">
          {page.category && <CatBadge label={page.category} />}
          <h2
            className="font-black leading-tight text-gray-900"
            style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem" }}
          >
            {page.title}
          </h2>
          {page.subtitle && (
            <p className="text-[10px] text-amber-700 italic mt-0.5">{page.subtitle}</p>
          )}
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 p-4 overflow-y-auto border-r border-gray-200">
            <BodyText text={page.body ?? ""} dropCap />
          </div>
          <div className="w-2/5 shrink-0 flex flex-col overflow-hidden">
            <div className="relative overflow-hidden shrink-0" style={{ height: "45%" }}>
              <img src={page.image} alt={page.title} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 p-3 overflow-y-auto">
              <BodyText text={page.body2 ?? ""} size="text-[11px]" />
            </div>
          </div>
        </div>
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function ArticleLeftLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="px-5 pt-3 pb-2 border-b border-gray-200 shrink-0">
          {page.category && <CatBadge label={page.category} />}
          <h2
            className="font-black leading-tight text-gray-900"
            style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem" }}
          >
            {page.title}
          </h2>
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="w-5/12 shrink-0 flex flex-col overflow-hidden border-r border-gray-200">
            <div className="relative overflow-hidden shrink-0" style={{ height: "50%" }}>
              <img src={page.image} alt={page.title} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 p-3 overflow-y-auto bg-blue-50">
              <BodyText text={page.body2 ?? ""} size="text-[11px]" />
            </div>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            <BodyText text={page.body ?? ""} dropCap />
          </div>
        </div>
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function ContinuationLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="bg-blue-50 border-b border-blue-200 px-5 py-1.5 shrink-0">
        <p className="text-[9px] text-blue-700 italic font-semibold">{page.subtitle}</p>
      </div>

      {page.pullQuote && (
        <div className="mx-5 my-3 border-l-4 border-blue-700 pl-3 bg-blue-50 py-2 shrink-0">
          <p
            className="text-[11px] text-blue-900 italic leading-relaxed"
            style={{ fontFamily: "Georgia,serif" }}
          >
            "{page.pullQuote}"
          </p>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 p-4 overflow-y-auto border-r border-gray-200">
          {page.category && <CatBadge label={page.category} />}
          <h2
            className="font-black text-gray-900 leading-tight mb-2"
            style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem" }}
          >
            {page.title}
          </h2>
          <BodyText text={page.body ?? ""} dropCap />
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          {page.image && (
            <div className="overflow-hidden shrink-0" style={{ height: "45%" }}>
              <img src={page.image} alt={page.title} className="w-full h-full object-cover" />
            </div>
          )}
          {page.body2 !== undefined && (
            <div className="flex-1 p-4 overflow-y-auto">
              <BodyText text={page.body2} size="text-[11px]" />
            </div>
          )}
        </div>
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function GalleryLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const images = page.galleryImages ?? [];
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="px-4 py-2 border-b border-gray-200 shrink-0">
        {page.category && <CatBadge label={page.category} />}
        <h2
          className="font-black text-gray-900"
          style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem" }}
        >
          {page.title}
        </h2>
        {page.subtitle && <p className="text-[9px] text-gray-500 italic">{page.subtitle}</p>}
      </div>
      <div className="grid grid-cols-3 gap-1 p-2 flex-1 overflow-hidden">
        {images.map((src, i) => (
          <button
            key={i}
            onClick={() => setLightbox(src)}
            className="relative overflow-hidden group focus:outline-none"
            style={{ aspectRatio: "4/3" }}
          >
            <img
              src={src}
              alt={`Photo ${i + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-blue-900/0 group-hover:bg-blue-900/30 transition-colors flex items-center justify-center">
              <span className="text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity">
                ⊕
              </span>
            </div>
          </button>
        ))}
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8"
            onClick={() => setLightbox(null)}
          >
            <motion.img
              src={lightbox}
              alt="Full"
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.85 }}
              className="max-w-full max-h-full object-contain rounded shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 text-sm"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SuccessStoryLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="flex shrink-0 border-b border-gray-200">
        <div className="bg-blue-900 text-amber-300 font-black uppercase tracking-widest text-[10px] px-4 py-2 flex items-center">
          {page.category}
        </div>
        <div className="flex-1 bg-amber-50 px-4 py-2 flex items-center">
          <p className="text-[11px] text-blue-900 font-bold uppercase tracking-wide">
            {page.title}
          </p>
        </div>
      </div>
      <div className="flex gap-0 shrink-0 border-b border-gray-200">
        <div className="flex-1 p-4">
          <BodyText text={page.body ?? ""} dropCap size="text-[12px]" />
        </div>
        <div className="w-2/5 overflow-hidden shrink-0">
          <img src={page.image} alt="Profile" className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {(page.sections ?? []).map((section, si) => (
          <div key={si} className="border-b border-gray-100">
            <div className="bg-green-700 px-4 py-1">
              <h3 className="text-[9px] font-black uppercase tracking-widest text-white">
                {section.heading}
              </h3>
            </div>
            <div className="px-4 py-3 grid grid-cols-2 gap-4">
              <BodyText
                text={section.body
                  .split("\n\n")
                  .slice(0, Math.ceil(section.body.split("\n\n").length / 2))
                  .join("\n\n")}
                size="text-[11px]"
              />
              <BodyText
                text={section.body
                  .split("\n\n")
                  .slice(Math.ceil(section.body.split("\n\n").length / 2))
                  .join("\n\n")}
                size="text-[11px]"
              />
            </div>
          </div>
        ))}
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function TextOnlyLayout({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader />
      <div className="px-5 py-3 border-b border-gray-200 shrink-0">
        {page.category && <CatBadge label={page.category} />}
        <h2
          className="font-black text-gray-900"
          style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem" }}
        >
          {page.title}
        </h2>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 p-4 overflow-y-auto border-r border-gray-200">
          <BodyText text={page.body ?? ""} dropCap />
        </div>
        {page.body2 && (
          <div className="flex-1 p-4 overflow-y-auto">
            <BodyText text={page.body2} />
          </div>
        )}
      </div>
      <TriFooter page={page} issueLabel={issueLabel} />
    </div>
  );
}

function BackCoverLayout({ page }: { page: TESCOMPage; issueLabel: string }) {
  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-blue-900 to-green-700 relative overflow-hidden">
      {page.image && (
        <img
          src={page.image}
          alt="back cover"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
      )}
      <div className="absolute inset-3 border-2 border-amber-400/30 opacity-40 pointer-events-none" />

      <div className="relative z-10 absolute top-8 left-8 bg-white/10 backdrop-blur rounded-full px-4 py-3 flex flex-col gap-2 items-center">
        {["🎓", "📚", "✨", "🏆"].map((s, i) => (
          <span key={i} className="text-xl">
            {s}
          </span>
        ))}
      </div>

      <div className="relative z-10 mt-auto p-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-amber-300 text-2xl">📞</span>
          <span
            className="text-white font-black"
            style={{ fontSize: "2.2rem", fontFamily: "Georgia,serif" }}
          >
            {page.body}
          </span>
        </div>
        <div className="text-blue-100 text-sm whitespace-pre-line leading-relaxed font-medium">
          {page.body2}
        </div>
      </div>
    </div>
  );
}

// ─── Page Router ──────────────────────────────────────────────────────────────

function PageContent({ page, issueLabel }: { page: TESCOMPage; issueLabel: string }) {
  switch (page.layout) {
    case "cover":
      return <CoverLayout page={page} issueLabel={issueLabel} />;
    case "editor":
      return <EditorLayout page={page} issueLabel={issueLabel} />;
    case "feature":
      return <FeatureLayout page={page} issueLabel={issueLabel} />;
    case "article-right":
      return <ArticleRightLayout page={page} issueLabel={issueLabel} />;
    case "article-left":
      return <ArticleLeftLayout page={page} issueLabel={issueLabel} />;
    case "gallery":
      return <GalleryLayout page={page} issueLabel={issueLabel} />;
    case "continuation":
      return <ContinuationLayout page={page} issueLabel={issueLabel} />;
    case "success-story":
      return <SuccessStoryLayout page={page} issueLabel={issueLabel} />;
    case "text-only":
      return <TextOnlyLayout page={page} issueLabel={issueLabel} />;
    case "back-cover":
      return <BackCoverLayout page={page} issueLabel={issueLabel} />;
    default:
      return <TextOnlyLayout page={page} issueLabel={issueLabel} />;
  }
}

const LAYOUT_ICON: Record<PageLayout, string> = {
  cover: "📘",
  editor: "✏️",
  article: "📄",
  "article-left": "📰",
  "article-right": "📰",
  gallery: "🖼",
  feature: "📋",
  "success-story": "⭐",
  "back-cover": "📗",
  continuation: "➡️",
  "text-only": "📄"
};

// ─── Main Magazine ────────────────────────────────────────────────────────────
//
// Self-contained: fetches the issue from Strapi on mount. No props required.
//   <TESCOMMagazine />                  → loads the most recently published issue
//   <TESCOMMagazine issueId={1} />      → loads a specific issue by id
//
// Hits GET /api/issues (your custom find() controller action), then picks
// the right issue client-side. No hardcoded demo data — a failed fetch shows
// a real error state instead of silently falling back to placeholder content.

interface TESCOMMagazineProps {
  /** Specific issue id to load. Omit to load the most recently published issue. */
  issueId?: number | string;
  tocDefaultOpen?: boolean;
}

export default function TESCOMMagazine({
  issueId,
  tocDefaultOpen = true
}: TESCOMMagazineProps = {}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState<"forward" | "backward">("forward");
  const [tocOpen, setTocOpen] = useState(tocDefaultOpen);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageScrollRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [pages, setPages] = useState<TESCOMPage[]>([]);
  const [issueLabel, setIssueLabel] = useState("TESCOM Magazine");
  const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "error">("loading");
  const [loadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoadStatus("loading");
      try {
        const rawIssue = await resolveIssue(issueId);

        if (cancelled) return;

        if (!rawIssue) {
          const seed = loadSeedIssue();
          setPages(seed.pages);
          setIssueLabel(seed.title);
          setCurrentIndex(0);
          setLoadStatus("ready");
          return;
        }

        const mapped = mapStrapiIssueToPages(rawIssue);
        setPages(mapped);
        setIssueLabel(rawIssue.title || "TESCOM Magazine");
        setCurrentIndex(0);
        setLoadStatus("ready");
      } catch (err) {
        if (cancelled) return;
        console.warn("[TESCOMMagazine] Strapi fetch failed, using local seed content:", err);
        const seed = loadSeedIssue();
        setPages(seed.pages);
        setIssueLabel(seed.title);
        setCurrentIndex(0);
        setLoadStatus("ready");
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [issueId]);

  const totalPages = pages.length;

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      void containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      void document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const goToIndex = useCallback(
    (idx: number, dir: "forward" | "backward") => {
      if (flipping || idx === currentIndex || idx < 0 || idx >= totalPages) return;
      setFlipDir(dir);
      setFlipping(true);
      timeoutRef.current = setTimeout(() => {
        setCurrentIndex(idx);
        setFlipping(false);
        setTimeout(() => pageScrollRef.current?.scrollTo({ top: 0, behavior: "instant" }), 20);
      }, 520);
    },
    [flipping, currentIndex, totalPages]
  );

  const flip = useCallback(
    (dir: "forward" | "backward") => {
      const next = dir === "forward" ? currentIndex + 1 : currentIndex - 1;
      goToIndex(next, dir);
    },
    [currentIndex, goToIndex]
  );

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") flip("forward");
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") flip("backward");
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [flip]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  // ─── Mobile detection ─────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-close TOC on mobile once a page has been picked
  const goToIndexRef = useRef<typeof goToIndex>();
  goToIndexRef.current = goToIndex;

  const selectPage = useCallback(
    (idx: number, dir: "forward" | "backward") => {
      goToIndexRef.current?.(idx, dir);
      if (isMobile) setTocOpen(false);
    },
    [isMobile]
  );

  // ─── Auto-flip when scroll reaches bottom ────────────────────────────────────
  // pageScrollRef lives on a motion.div inside AnimatePresence, which unmounts
  // and remounts on every page change — a plain useEffect's listener goes
  // stale after the first flip. A ref callback re-attaches on every mount.
  const flipRef = useRef(flip);
  flipRef.current = flip;
  const flippingRef = useRef(flipping);
  flippingRef.current = flipping;
  const hasMoreRef = useRef(currentIndex < totalPages - 1);
  hasMoreRef.current = currentIndex < totalPages - 1;

  const scrollListenerCleanupRef = useRef<(() => void) | null>(null);

  const setPageScrollRef = useCallback((node: HTMLDivElement | null) => {
    // Clean up the previous listener (previous page's node, if any)
    scrollListenerCleanupRef.current?.();
    scrollListenerCleanupRef.current = null;
    pageScrollRef.current = node;
    if (!node) return;

    let triggered = false;

    const onScroll = () => {
      if (triggered || flippingRef.current || !hasMoreRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = node;
      // If content doesn't overflow, scrollHeight ~= clientHeight; guard against div-by-zero / instant trigger
      if (scrollHeight <= clientHeight + 4) return;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      if (distanceFromBottom < 60) {
        triggered = true;
        flipRef.current("forward");
      }
    };

    node.addEventListener("scroll", onScroll, { passive: true });
    scrollListenerCleanupRef.current = () => node.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(
    () => () => {
      scrollListenerCleanupRef.current?.();
    },
    []
  );

  const currentPage = pages[currentIndex];

  if (loadStatus === "loading") {
    return (
      <div className="h-screen bg-gray-900 flex items-center justify-center text-gray-400 text-sm">
        Loading magazine…
      </div>
    );
  }

  if (loadStatus === "error") {
    return (
      <div className="h-screen bg-gray-900 flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
        <p>Couldn't load the magazine.</p>
        <p className="text-gray-600 text-xs">{loadError}</p>
      </div>
    );
  }

  if (!currentPage) {
    return (
      <div className="h-screen bg-gray-900 flex items-center justify-center text-gray-400 text-sm">
        No pages to display.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-screen bg-gray-900 flex flex-col overflow-hidden"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <header className="bg-gradient-to-r from-blue-900 to-green-800 border-b border-blue-700 px-4 py-2 flex items-center justify-between shrink-0 z-30">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setTocOpen((o) => !o)}
            className="flex items-center gap-1.5 text-amber-300 hover:text-amber-200 transition-colors text-xs font-semibold"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h7"
              />
            </svg>
            Contents
          </button>
          <div className="h-4 w-px bg-blue-700" />
          <span className="text-green-300 text-[10px] uppercase tracking-widest font-semibold">
            {issueLabel}
          </span>
          {currentPage && (
            <>
              <div className="h-4 w-px bg-blue-700" />
              <span className="text-[10px] text-amber-300 font-medium truncate max-w-xs">
                {LAYOUT_ICON[currentPage.layout]} {currentPage.title}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-blue-300 text-[10px] tabular-nums">
            {currentIndex + 1} / {totalPages}
          </span>
          <div className="flex gap-1">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => goToIndex(i, i > currentIndex ? "forward" : "backward")}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === currentIndex ? "bg-amber-400 w-4" : "bg-blue-700 hover:bg-green-500 w-1.5"
                }`}
              />
            ))}
          </div>
          <div className="h-4 w-px bg-blue-700" />
          <button
            onClick={toggleFullscreen}
            className="text-green-300 hover:text-amber-300 transition-colors p-1"
          >
            {isFullscreen ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5M15 15l5.25 5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        <AnimatePresence>
          {tocOpen && isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-30"
              onClick={() => setTocOpen(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {tocOpen && (
            <motion.aside
              initial={isMobile ? { x: -300, opacity: 0 } : { width: 0, opacity: 0 }}
              animate={isMobile ? { x: 0, opacity: 1 } : { width: 260, opacity: 1 }}
              exit={isMobile ? { x: -300, opacity: 0 } : { width: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className={
                isMobile
                  ? "fixed top-0 left-0 bottom-0 w-[78vw] max-w-[300px] bg-gray-950 border-r border-blue-900 overflow-hidden flex flex-col z-40 shadow-2xl"
                  : "bg-gray-950 border-r border-blue-900 overflow-hidden shrink-0 flex flex-col z-20"
              }
            >
              <div className="p-4 border-b border-blue-900 flex items-center justify-between">
                <span className="text-[9px] text-amber-400 uppercase tracking-[0.3em] font-bold">
                  Table of Contents
                </span>
                {isMobile && (
                  <button
                    onClick={() => setTocOpen(false)}
                    className="text-gray-500 hover:text-gray-300 p-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto py-1">
                {pages.map((p, idx) => (
                  <button
                    key={p.id}
                    onClick={() => selectPage(idx, idx > currentIndex ? "forward" : "backward")}
                    className={`w-full text-left px-3 py-2.5 transition-all flex items-start gap-2 border-l-2 ${
                      idx === currentIndex
                        ? "bg-blue-900/40 border-amber-400"
                        : "hover:bg-blue-900/20 border-transparent"
                    }`}
                  >
                    <span className="text-sm shrink-0 mt-0.5">{LAYOUT_ICON[p.layout]}</span>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-[10px] font-medium leading-snug ${
                          idx === currentIndex
                            ? "text-amber-300"
                            : "text-gray-400 group-hover:text-gray-200"
                        }`}
                      >
                        {p.title.length > 55 ? p.title.slice(0, 55) + "…" : p.title}
                      </p>
                      {p.category && (
                        <span className="text-[8px] text-green-500 uppercase tracking-wider">
                          {p.category}
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-[9px] font-bold shrink-0 tabular-nums ${
                        idx === currentIndex ? "text-amber-400" : "text-gray-700"
                      }`}
                    >
                      {String(p.pageNumber).padStart(2, "0")}
                    </span>
                  </button>
                ))}
              </div>
              <div className="p-3 border-t border-blue-900">
                <p className="text-[8px] text-gray-600 text-center tracking-wider">
                  ← → KEYS TO NAVIGATE
                </p>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        <main
          className="flex-1 flex items-center justify-center bg-gray-800 relative overflow-hidden"
          style={{ padding: isFullscreen ? "1.5rem 4rem" : "1rem 2.5rem" }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-700/10 rounded-full blur-3xl" />
          </div>

          <div
            className="relative w-full h-full flex items-center justify-center"
            style={{ perspective: "2400px" }}
          >
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2/3 h-5 bg-black/60 blur-2xl rounded-full pointer-events-none" />

            <div
              className="relative flex h-full w-full max-w-4xl"
              style={{ filter: "drop-shadow(0 25px 55px rgba(0,0,0,0.8))" }}
            >
              <div className="w-4 bg-gradient-to-r from-blue-950 via-blue-800 to-green-900 rounded-l-sm shrink-0 self-stretch relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/30" />
                <div className="h-full flex items-center justify-center">
                  <span
                    className="text-blue-300 text-[8px] font-black tracking-widest uppercase"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    TESCOM
                  </span>
                </div>
              </div>

              <div className="relative flex-1 overflow-hidden rounded-r-sm">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    ref={setPageScrollRef}
                    className="absolute inset-0 overflow-y-auto"
                    initial={
                      flipDir === "forward"
                        ? { rotateY: -90, opacity: 0.7 }
                        : { rotateY: 90, opacity: 0.7 }
                    }
                    animate={{ rotateY: 0, opacity: 1 }}
                    exit={
                      flipDir === "forward"
                        ? { rotateY: 90, opacity: 0.5 }
                        : { rotateY: -90, opacity: 0.5 }
                    }
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                    style={{
                      transformStyle: "preserve-3d",
                      backfaceVisibility: "hidden",
                      transformOrigin: flipDir === "forward" ? "left center" : "right center"
                    }}
                  >
                    {currentPage && <PageContent page={currentPage} issueLabel={issueLabel} />}
                    <div
                      className="sticky right-0 top-0 float-right w-3 h-full pointer-events-none"
                      style={{
                        background: "linear-gradient(to left,rgba(0,0,0,0.1),transparent)",
                        marginLeft: "-0.75rem"
                      }}
                    />
                    <div
                      className="absolute left-0 top-0 bottom-0 w-2 pointer-events-none z-10"
                      style={{
                        background: "linear-gradient(to right,rgba(0,0,0,0.15),transparent)"
                      }}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <button
              onClick={() => flip("backward")}
              disabled={currentIndex === 0 || flipping}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-gray-900/90 border border-gray-700 text-gray-300 hover:bg-blue-900 hover:text-amber-300 disabled:opacity-20 transition-all flex items-center justify-center z-20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={() => flip("forward")}
              disabled={currentIndex === totalPages - 1 || flipping}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-gray-900/90 border border-gray-700 text-gray-300 hover:bg-blue-900 hover:text-amber-300 disabled:opacity-20 transition-all flex items-center justify-center z-20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
